import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../sidebar/Sidebar";
import Loading from "../common/Loading";
import "./Appliedjobs.css";
import ProgressTracker from "../ProgressTracker/ProgressTracker";   // correct import paths
import StatusLabel from "./StatusLabel";
import axios from 'axios';
import { getProfile } from '../../../../helpers/hasToken';
import moment from 'moment';
import { getCandidateStatsAndHistory } from '../dataCalls';

const Appliedjobs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTracker, setShowTracker] = useState({});
  const [dynamicStatus, setDynamicStatus] = useState({}); 
  const [loadingStatus, setLoadingStatus] = useState({}); 
  const [errorStatus, setErrorStatus] = useState({}); 
  const [categories, setCategories] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [jobStatuses, setJobStatuses] = useState({});
  const API_URL = process.env.REACT_APP_API_SERVICE;

  useEffect(() => {
    const profile = getProfile();
    if (profile && profile.user_id) {
      // Fetch history
      axios.get(`${API_URL}/users/history/${profile.user_id}`)
        .then(res => {
          if (res.status === 200) {
            const mappedJobs = res.data.data.map(item => ({
              id: item.jobId,
              title: item.title,
              location: item.location,
              role: item.type, // status type as role
              company: item.tenant,
              logo: item.logo,
              postedDate: moment.utc(item.created).format('MMM Do, YYYY'),
              type: item.type, // keep original type for tracker
              schema: item.schema_name || 'candidate'
            }));
            setJobs(mappedJobs);
          }
        })
        .catch(error => {
          console.error('Error fetching applied jobs:', error);
        })
        .finally(() => {
          setLoading(false);
        });

      // Fetch statuses for all jobs
      setStatusLoading(true);
      getCandidateStatsAndHistory(profile.user_id)
        .then(res => {
          if (res.status === 200 && res.data && res.data.data) {
            const statuses = {};
            res.data.data.forEach(item => {
              if (item.jobId && item.status) {
                const { currentStep, status } = processStatusData(item.status);
                statuses[item.jobId] = { currentStep, status };
              }
            });
            setJobStatuses(statuses);
          }
        })
        .catch(error => {
          console.error('Error fetching candidate statuses:', error);
        })
        .finally(() => {
          setStatusLoading(false);
        });
    } else {
      setLoading(false);
      setStatusLoading(false);
    }
  }, []);

  // New useEffect to fetch categories
  useEffect(() => {
    axios.get(`${API_URL}/jobcategory`)
      .then(res => {
        if (res.status === 200) {
          setCategories(res.data.data);
        }
      })
      .catch(error => {
        console.error('Error fetching categories:', error);
      });
  }, []);

  const getTypeString = (type) => {
    switch (type) {
      case 0: return 'Submitted';
      case 1: return 'Screening';
      case 2: return 'Interview';
      case 3: return 'offered';
      case 4: return 'Position Filled';
      default: return 'Unknown';
    }
  };

  const getTrackerProps = (type) => {
    switch (type) {
      case 'applied': return { currentStep: 1, status: 'success' };
      case 'reviewed': return { currentStep: 2, status: 'success' };
      case 'interview': return { currentStep: 3, status: 'success' };
      default: return { currentStep: 2, status: 'failed' };
    }
  };

  // Process API response to determine currentStep and status for ProgressTracker
  const processStatusData = (statusData) => {
    if (!statusData || statusData.length === 0) {
      return { currentStep: 0, status: 'failed' };
    }
  const stepLabels = [
    "Submitted",
    "Screening",
    "Interview",
    "Position Filled",
    "offered",
  ];
    let highestStep = -1;
    let failed = false;
    statusData.forEach(item => {
      const index = stepLabels.findIndex(step => step.toLowerCase() === getTypeString(item.type).toLowerCase());
      if (index > highestStep) {
        highestStep = index;
        // Assume success unless type indicates rejection (e.g., type 4)
        if (item.type === 4) {
          failed = true;
        }
      }
    });
    return {
      currentStep: highestStep >= 0 ? highestStep : 0,
      status: failed ? 'failed' : 'success'
    };
  };

  // Filter jobs based on search term
  const filteredJobs = jobs.filter(job =>
    (job.title ? job.title.toLowerCase() : '').includes(searchTerm.toLowerCase()) ||
    (job.company ? job.company.toLowerCase() : '').includes(searchTerm.toLowerCase())
  );

  // Toggle tracker for a specific job id and fetch dynamic status if toggling on
  const toggleTracker = (jobId, job) => {
    const isShowing = showTracker[jobId];
    setShowTracker(prev => ({
      ...prev,
      [jobId]: !prev[jobId],
    }));

    if (!isShowing && !jobStatuses[jobId]) {
      // Only fetch if not pre-loaded
      setLoadingStatus(prev => ({ ...prev, [jobId]: true }));
      setErrorStatus(prev => ({ ...prev, [jobId]: null }));
      const profile = getProfile();
      if (profile && profile.user_id) {
        console.log('API URL:', process.env.REACT_APP_API_SERVICE);
        console.log('User ID:', profile.user_id);
        const token = localStorage.getItem('token');
        console.log('Token:', token);
        getCandidateStatsAndHistory(profile.user_id)
          .then(res => {
            console.log('Candidate stats and history response:', res);
            if (res.status === 200 && res.data && res.data.data) {
              // Find the status for the specific jobId from the API response
              const jobStatusData = res.data.data.find(i => i.jobId === jobId);
              if (jobStatusData && jobStatusData.status) {
                const { currentStep, status } = processStatusData(jobStatusData.status);
                setDynamicStatus(prev => ({ ...prev, [jobId]: { currentStep, status } }));
                // Also update jobStatuses for consistency
                setJobStatuses(prev => ({ ...prev, [jobId]: { currentStep, status } }));
              } else {
                setErrorStatus(prev => ({ ...prev, [jobId]: 'No status found for this job' }));
              }
            } else {
              console.error('Unexpected response data:', res);
              setErrorStatus(prev => ({ ...prev, [jobId]: 'Failed to fetch status' }));
            }
          })
          .catch(error => {
            console.error('Error fetching candidate stats and history:', error);
            setErrorStatus(prev => ({ ...prev, [jobId]: 'Error fetching status' }));
          })
          .finally(() => {
            setLoadingStatus(prev => ({ ...prev, [jobId]: false }));
          });
      } else {
        setErrorStatus(prev => ({ ...prev, [jobId]: 'User not logged in' }));
        setLoadingStatus(prev => ({ ...prev, [jobId]: false }));
      }
    } else if (!isShowing && jobStatuses[jobId]) {
      // Use pre-fetched status
      setDynamicStatus(prev => ({ ...prev, [jobId]: jobStatuses[jobId] }));
    }
  };

  if (loading) {
    return <Loading message="Please wait while your data is loading" />;
  }

  return (
    <div className="appliedjobs">
      <Sidebar />
      <div className="appliedjobs-content">
        {/* Header with search */}
        <div className="Tot-Applied-Job">
          <div className="NameText">
            <h1>
              Applied Jobs <strong>({filteredJobs.length})</strong>
            </h1>
          </div>
          <div className="TR-searchbox">
            <input
              type="text"
              placeholder="Search in applied Jobs"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img
              src={require('../../../img/UserProfile/search-icon-Appliedjobs.webp')}
              alt="search"
              className="search-icon"
            />
          </div>
        </div>

        {/* Render job cards dynamically or message if no jobs */}
        {filteredJobs.length === 0 ? (
          <div className="no-jobs-message">
            <p><span className="first-part">You haven't applied to any jobs yet.</span> <br /><span className="second-part">Start exploring opportunities & apply to jobs that match your skills!</span></p>
          </div>
        ) : (
          filteredJobs.map((job) => (
          <div key={job.id} className="appliedjobs-card">
            <div className="CardParent">
              <div className="CardChild-left">
                <h2>{job.title}</h2>
                <div className="JoB-Details">
                  <div className="JOBID MB-10">
                    <p>
                      <strong>Job ID:</strong> {job.id}
                    </p>
                  </div>
                  <div className="Statecity MB-10">
                    <picture>
                      <source srcSet={require('../../../img/UserProfile/location-icon-purple.webp')} type="image/webp" />
                      <source srcSet={require('../../../img/UserProfile/location-icon-purple.webp')} type="image/png" />
                      <img
                        src={require('../../../img/UserProfile/location-icon-purple.webp')}
                        alt="location icon"
                        className="icon"
                      />
                    </picture>
                    <p>{job.location}</p>
                  </div>
                  <div className="Role-forJob MB-10">
                    {/* <p>{getRoleLabel(job.role)}</p> */}
                  </div>
                  <div className="USACompanyName MB-10">
                    {job.logo ? (
                      <picture>
                        <source srcSet={job.logo} type="image/webp" />
                        <img
                          src={job.logo}
                          alt="Company Logo"
                          className="icon"
                        />
                      </picture>
                    ) : null}
                    <p>{job.company}</p>
                  </div>
                  <div className="postedDate MB-10">
                    <picture>
                      <source srcSet={require('../../../img/UserProfile/posted-date.webp')} type="image/webp" />
                      <source srcSet={require('../../../img/UserProfile/posted-date.webp')} type="image/png" />
                      <img
                        src={require('../../../img/UserProfile/posted-date.webp')}
                        alt="posted date"
                        className="icon"
                      />
                    </picture>
                    <p>
                      Posted on: <strong>{job.postedDate}</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="CardChild-right">
                <div className="job-actions">
                  <button className="applied-CardChild-right-btn" onClick={() => navigate(`/job-details/${job.id}/${job.schema}`)}>Show Full Job Details</button>

                  <button
                    className="applied-CardChild-right-btn1"
                    onClick={() => toggleTracker(job.id, job)} // toggle tracker for this job
                    disabled={loadingStatus[job.id]}
                  >
                    {loadingStatus[job.id] ? 'Loading...' : 'Check Current Application Status'}
                  </button>

                  {/* Show tracker only when button clicked and loading complete */}
                  {showTracker[job.id] && !loadingStatus[job.id] && (
                    errorStatus[job.id] ? (
                      <p style={{ color: 'red' }}>{errorStatus[job.id]}</p>
                    ) : dynamicStatus[job.id] ? (
                      <>
                        <ProgressTracker {...dynamicStatus[job.id]} />
                        <StatusLabel currentStep={dynamicStatus[job.id].currentStep} />
                      </>
                    ) : null
                  )}
                </div>
              </div>
            </div>
          </div>
        ))
        )}
      </div>
    </div>
  );

  function getRoleLabel(role) {
    if (!role || role === "0") {
      return "Uncategorized";
    }
    // Map role to category name from categories list
    const category = categories.find(cat => cat.catId === role || cat.catName === role);
    if (category) {
      return category.catName;
    }
    return "Uncategorized";
  }
};

export default Appliedjobs;
